import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { track } from "@/lib/track";
import { ArrowRight, MapPin, Train, Plane, AlertTriangle, Check, Lightbulb } from "lucide-react";
import { CITY_IMAGES } from "@/data/cities";
import { CONTACT } from "@/data/site";

const QUARTIERS = [
  { name: "Part-Dieu", text: "Le quartier d'affaires : tours, hôtels et salles de réunion à deux pas de la gare. Idéal pour séminaires, conventions et événements corporate en semaine." },
  { name: "Presqu'île", text: "Le cœur historique et commerçant : Palais de la Bourse, Grand Hôtel-Dieu, cours et hôtels particuliers pour cocktails et galas en plein centre." },
  { name: "Confluence", text: "Le quartier contemporain au sud : La Sucrière, le Musée des Confluences et de grands plateaux modulables pour soirées et lancements spectaculaires." },
  { name: "Cité internationale", text: "Centre de Congrès, amphithéâtre et hôtels au bord du parc de la Tête d'Or : le pôle naturel des congrès et conventions à Lyon." },
  { name: "Vieux-Lyon & Fourvière", text: "Traboules, quais de Saône et lieux patrimoniaux : un cadre unique pour dîners clients et événements VIP à taille humaine." },
  { name: "Est lyonnais & Eurexpo", text: "Eurexpo, le Groupama Stadium et les zones d'activités : grands formats, salons professionnels et événements logistiques." },
];

const LIEUX = [
  { name: "La Sucrière", detail: "Grands formats, soirées et expositions — Confluence" },
  { name: "Musée des Confluences", detail: "Soirées d'exception et événements presse" },
  { name: "Palais de la Bourse", detail: "Galas et cérémonies — Presqu'île" },
  { name: "Grand Hôtel-Dieu", detail: "Cocktails et dîners en plein centre" },
  { name: "Centre de Congrès — Cité internationale", detail: "Congrès, conventions et plénières" },
  { name: "Halle Tony Garnier", detail: "Très grands formats" },
  { name: "Eurexpo", detail: "Salons professionnels et conventions — est lyonnais" },
  { name: "Groupama Stadium", detail: "Salons et événements avec vue sur l'enceinte" },
  { name: "Le Transbordeur", detail: "Soirées et formats créatifs — Villeurbanne" },
  { name: "Péniches & berges de Saône", detail: "Afterworks et réceptions estivales" },
];

const CONSEILS = [
  "Réservez les lieux emblématiques (Sucrière, Bourse, Confluences) 3 à 6 mois à l'avance, davantage en période de salon.",
  "En centre-ville, anticipez les autorisations pour berges, cours intérieures et livraisons : elles prennent plusieurs semaines.",
  "Prévoyez un plan B couvert pour les réceptions en extérieur — le climat lyonnais est changeant au printemps et en automne.",
  "Les jours de grand salon à Eurexpo, hôtels et traiteurs sont tendus : caler votre date tôt sécurise les tarifs.",
];

export default function CityLyon() {
  return (
    <>
      <Seo
        title="Organiser un Événement d'Entreprise à Lyon | Lieux & Conseils"
        description="Guide pratique pour organiser un événement d'entreprise à Lyon : quartiers, lieux et capacités, accès, logistique et conseils. Par une agence basée à Lyon Part-Dieu."
        path="/villes/lyon"
      />
      <PageHero eyebrow="Lyon — guide événementiel" title={<>Organiser un événement<br /><span className="italic text-lime-dim">d'entreprise à Lyon</span></>} />

      {/* Intro */}
      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10" data-testid="lyon-guide-intro">
        <div className="grid items-start gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <p className="max-w-2xl text-lg leading-relaxed text-ink/70">
                Deuxième pôle économique français, Lyon concentre sièges sociaux, ETI industrielles et scale-ups — et donc des centaines d'événements d'entreprise chaque année. Ce guide rassemble ce qu'il faut savoir avant de lancer le vôtre : les quartiers adaptés à chaque format, les lieux que nous pratiquons, les accès et les contraintes à anticiper.
              </p>
            </Reveal>
            <Reveal delay={1}>
              <div className="mt-8 rounded-2xl border border-lime-dim/40 bg-lime/10 p-6" data-testid="lyon-guide-agence-link">
                <p className="text-sm leading-relaxed text-ink/70">
                  Vous cherchez une équipe pour tout coordonner ?{" "}
                  <Link to="/agence-evenementielle-entreprise-lyon" className="font-medium text-ink underline decoration-lime-dim decoration-2 underline-offset-4 hover:text-lime-dim">
                    Découvrir notre agence événementielle à Lyon
                  </Link>
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={1} className="md:col-span-5">
            <img src={CITY_IMAGES.lyon} alt="Lyon — la Saône et la basilique de Fourvière" className="aspect-[4/3] w-full rounded-2xl object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>

      {/* Quartiers */}
      <section className="border-t border-ink/10 bg-mist" data-testid="lyon-guide-quartiers">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Où organiser</span></Reveal>
          <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Les quartiers, selon votre format</h2></Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {QUARTIERS.map((q, i) => (
              <Reveal key={q.name} delay={i % 3}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-7">
                  <h3 className="flex items-center gap-2 font-serif text-xl text-ink"><MapPin size={16} className="text-lime-dim" />{q.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{q.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lieux */}
      <section className="border-t border-ink/10 bg-white" data-testid="lyon-guide-lieux">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Repérés pour vous</span></Reveal>
          <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Les lieux événementiels que nous pratiquons</h2></Reveal>
          <div className="mt-10 grid gap-3 sm:grid-cols-2">
            {LIEUX.map((l, i) => (
              <Reveal key={l.name} delay={i % 2}>
                <div className="flex items-start gap-3 rounded-xl border border-ink/10 bg-mist px-5 py-4">
                  <Check size={15} className="mt-1 shrink-0 text-lime-dim" />
                  <div>
                    <p className="font-medium text-ink">{l.name}</p>
                    <p className="text-sm text-ink/55">{l.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={2}><p className="mt-6 max-w-2xl text-sm text-ink/50">Et bien sûr vos propres locaux : inaugurations, afterworks et soirées internes y sont souvent les formats les plus efficaces — nous apportons tout sur place.</p></Reveal>
        </div>
      </section>

      {/* Accès & logistique */}
      <section className="border-t border-ink/10 bg-ink text-white" data-testid="lyon-guide-acces">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={`${overline} !text-lime`}>Accès & logistique</span></Reveal>
          <div className="mt-10 grid gap-px overflow-hidden rounded-2xl bg-white/10 md:grid-cols-3">
            <Reveal>
              <div className="h-full bg-ink p-8">
                <Train size={20} className="text-lime" />
                <h3 className="mt-4 font-serif text-xl">Le rail</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">Gares Part-Dieu et Perrache au cœur de la ville, Lyon à 2 h de Paris en TGV : vos invités d'Île-de-France viennent dans la journée sans contrainte.</p>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <div className="h-full bg-ink p-8">
                <Plane size={20} className="text-lime" />
                <h3 className="mt-4 font-serif text-xl">L'air</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">Aéroport Lyon-Saint-Exupéry relié au centre par le Rhônexpress : pratique pour les équipes venues de toute la France et de l'Europe.</p>
              </div>
            </Reveal>
            <Reveal delay={2}>
              <div className="h-full bg-ink p-8">
                <AlertTriangle size={20} className="text-lime" />
                <h3 className="mt-4 font-serif text-xl">Les contraintes à anticiper</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">Stationnement et livraisons en Presqu'île, autorisations pour berges et cours, nuisances sonores en centre-ville, affluence autour d'Eurexpo les jours de salon.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Conseils pratiques */}
      <section className="border-t border-ink/10 bg-mist" data-testid="lyon-guide-conseils">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Conseils pratiques</span></Reveal>
          <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Ce que nous recommandons à Lyon</h2></Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {CONSEILS.map((c, i) => (
              <Reveal key={c.slice(0, 30)} delay={i % 2}>
                <div className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-white p-6">
                  <Lightbulb size={16} className="mt-1 shrink-0 text-lime-dim" />
                  <p className="text-sm leading-relaxed text-ink/65">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28" data-testid="lyon-guide-cta">
        <Reveal><h2 className="max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">Un projet d'événement à Lyon ? <span className="italic text-lime-dim">Parlons-en.</span></h2></Reveal>
        <Reveal delay={1}><p className="mt-5 max-w-xl text-lg text-ink/60">Brief en 2 minutes. Réponse sous 24 h ouvrées. Sans engagement.</p></Reveal>
        <Reveal delay={2}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/devis" onClick={() => track("click_devis")} className={btnPrimary} data-testid="lyon-guide-devis-btn">Recevoir une première proposition <ArrowRight size={16} /></Link>
            <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className={btnOutline}>Appeler le {CONTACT.phoneMobile}</a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
